import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircleFill, BsEye } from 'react-icons/bs';

import SampleRecipientField from '#contactListFeature/components/SampleRecipientField/SampleRecipientField';
import { sampleRecipientRef } from '#contactListFeature/components/SampleRecipientField/sampleRecipientRef';
import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';

import { generatePreviewPdf } from '../../api/postMailingApi';
import { openPdfBlob } from '../../utils/openPdfBlob';

import SkippedRecipientsNotice from './SkippedRecipientsNotice';

function ChecklistRow({ ok, label }) {
	return (
		<li className="flex items-center gap-2 text-sm">
			{ok ? (
				<BsCheckCircleFill className="h-4 w-4 flex-shrink-0 text-success-700" />
			) : (
				<BsEye className="h-4 w-4 flex-shrink-0 text-caution-500" />
			)}
			<span className={ok ? 'text-text' : 'text-caution-700'}>{label}</span>
		</li>
	);
}

ChecklistRow.propTypes = {
	ok: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
};

/**
 * Modal showing cost breakdown before confirming a post-mailing send.
 */
export default function CostConfirmationModal({
	isOpen,
	onClose,
	onConfirm,
	campaignId,
	listIds,
	customVariableFallbacks,
	costData,
	isLoading,
	isError,
	onRetry,
	isSending,
}) {
	const showError = useErrorStore((s) => s.showError);
	const [proofLoading, setProofLoading] = useState(false);
	const [proofViewed, setProofViewed] = useState(false);
	const [proofRecipient, setProofRecipient] = useState(null);

	useEffect(() => {
		if (!isOpen) setProofViewed(false);
	}, [isOpen]);

	// Render the final letter (sample or chosen recipient + the org's real profile) so the user can proof
	// it before the irreversible, paid send.
	const handleProof = async () => {
		if (!campaignId || proofLoading) return;
		setProofLoading(true);
		try {
			const blob = await generatePreviewPdf(campaignId, sampleRecipientRef(proofRecipient));
			openPdfBlob(blob);
			setProofViewed(true);
		} catch (err) {
			showError({ error: err, title: 'Musterbrief konnte nicht erstellt werden' });
		} finally {
			setProofLoading(false);
		}
	};

	if (isLoading) {
		return (
			<Modal isOpen={isOpen} onClose={onClose}>
				<Panel.Header onClose={onClose}>Postmailing versenden</Panel.Header>
				<Panel.Body>
					<div className="flex justify-center py-8">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
					</div>
				</Panel.Body>
			</Modal>
		);
	}

	if (isError || !costData) {
		return (
			<Modal isOpen={isOpen} onClose={onClose}>
				<Panel.Header onClose={onClose}>Postmailing versenden</Panel.Header>
				<Panel.Body>
					<p className="text-sm text-text">
						Die Kosten konnten nicht berechnet werden. Bitte versuchen Sie es erneut — es wurde nichts
						versendet oder berechnet.
					</p>
				</Panel.Body>
				<Panel.Footer className="justify-end">
					<Button variant="outline" onClick={onClose}>
						Abbrechen
					</Button>
					{onRetry && (
						<Button variant="solid" onClick={onRetry}>
							Erneut versuchen
						</Button>
					)}
				</Panel.Footer>
			</Modal>
		);
	}

	const {
		recipientCount,
		pricePerUnit,
		currency,
		totalCost,
		duplex,
		sheetCount,
		offerRequired,
		skippedMissingVariables,
	} = costData;
	const skippedNotice = campaignId && (
		<SkippedRecipientsNotice
			campaignId={campaignId}
			skipped={skippedMissingVariables}
			customVariableFallbacks={customVariableFallbacks}
		/>
	);

	const formattedPrice = new Intl.NumberFormat('de-DE', {
		style: 'currency',
		currency: currency || 'EUR',
	});

	// Special-format PDFs have no self-service price — the send routes to a manual offer. Show a request
	// dialog instead of the cost breakdown / payment step (no proof either: nothing gets auto-stamped).
	if (offerRequired) {
		return (
			<Modal isOpen={isOpen} onClose={onClose}>
				<Panel.Header onClose={onClose}>Angebot anfordern</Panel.Header>
				<Panel.Body className="space-y-4">
					<Alert tone="caution">
						Ihr Design ist ein <span className="font-medium">Sonderformat</span> (kein einseitiges A4).
						Solche Mailings versenden wir nicht automatisch — wir prüfen Ihr Design und erstellen ein
						individuelles Angebot. Es entstehen jetzt keine Kosten.
					</Alert>
					<div className="bg-bgBase-200 rounded-card p-4">
						<div className="flex justify-between text-sm">
							<span className="text-text">Empfänger</span>
							<span className="font-medium">{recipientCount}</span>
						</div>
					</div>
					{skippedNotice}
				</Panel.Body>
				<Panel.Footer className="justify-end">
					<Button variant="outline" onClick={onClose} disabled={isSending}>
						Abbrechen
					</Button>
					<Button variant="solid" onClick={onConfirm} disabled={isSending} loading={isSending}>
						Angebot anfordern
					</Button>
				</Panel.Footer>
			</Modal>
		);
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<Panel.Header onClose={onClose}>Postmailing versenden</Panel.Header>
			<Panel.Body className="space-y-4">
				<div className="bg-bgBase-200 rounded-card p-4 space-y-2">
					<div className="flex justify-between text-sm">
						<span className="text-text">Empfänger</span>
						<span className="font-medium">{recipientCount}</span>
					</div>
					<div className="flex justify-between text-sm">
						<span className="text-text">Stückpreis</span>
						<span className="font-medium">{formattedPrice.format(pricePerUnit)}</span>
					</div>
					{duplex && (
						<div className="flex justify-between text-xs text-bgBase-800">
							<span>inkl. Rückseite (beidseitiger Druck)</span>
						</div>
					)}
					{sheetCount > 1 && (
						<div className="flex justify-between text-xs text-bgBase-800">
							<span>{sheetCount} Blätter (inkl. Mehrblatt-Zuschlag)</span>
						</div>
					)}
					<div className="border-t border-bgBase-400 my-2" />
					<div className="flex justify-between text-md">
						<span className="font-semibold text-text">Gesamtkosten</span>
						<span className="font-bold text-text">
							{formattedPrice.format(totalCost)}
							<span className="text-xs font-normal text-bgBase-800 ml-1">zzgl. MwSt.</span>
						</span>
					</div>
				</div>

				{skippedNotice}

				<p className="text-sm text-text">
					Sie werden im nächsten Schritt sicher zur Zahlung weitergeleitet (Karte, SEPA-Lastschrift oder
					PayPal).
				</p>

				<Alert tone="info">
					Bei Zahlung per <span className="font-medium">SEPA-Lastschrift</span> werden die Briefe erst nach
					Zahlungseingang versendet (in der Regel 3–6 Werktage). Bei Karte und PayPal erfolgt der Versand
					sofort.
				</Alert>

				{/* Pre-payment checklist — every row must be green before payment; the only actionable
				    item is proofing the letter (design + sender are guaranteed by the send gate) */}
				<ul className="space-y-1.5 rounded-card border border-bgBase-400 bg-white p-3">
					<ChecklistRow ok label="Brief-Design vorhanden" />
					<ChecklistRow ok label="Absenderadresse hinterlegt" />
					<ChecklistRow
						ok={proofViewed}
						label={proofViewed ? 'Musterbrief geprüft' : 'Musterbrief noch nicht angesehen'}
					/>
				</ul>

				<SampleRecipientField
					label="Musterbrief für"
					recipient={proofRecipient}
					onChange={setProofRecipient}
					listIds={listIds}
					disabled={proofLoading || isSending}
				/>

				<Button
					fullWidth
					variant="outline"
					onClick={handleProof}
					disabled={proofLoading || isSending}
					loading={proofLoading}
				>
					{proofLoading ? 'Musterbrief wird erstellt …' : 'Musterbrief als PDF ansehen'}
				</Button>

				{!proofViewed && (
					<p className="text-xs text-bgBase-800 text-right">
						Bitte sehen Sie sich zuerst den Musterbrief an — erst danach ist die Zahlung möglich.
					</p>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose} disabled={isSending}>
					Abbrechen
				</Button>
				<Button variant="solid" onClick={onConfirm} disabled={isSending || !proofViewed} loading={isSending}>
					Weiter zur Zahlung
				</Button>
			</Panel.Footer>
		</Modal>
	);
}

CostConfirmationModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	campaignId: PropTypes.string,
	// The campaign's contact lists; the proof recipient is picked from these.
	listIds: PropTypes.arrayOf(PropTypes.string),
	customVariableFallbacks: PropTypes.object,
	costData: PropTypes.shape({
		recipientCount: PropTypes.number.isRequired,
		// Price fields are absent for special-format campaigns (offerRequired) — a manual offer, no price.
		pricePerUnit: PropTypes.number,
		currency: PropTypes.string,
		totalCost: PropTypes.number,
		duplex: PropTypes.bool,
		sheetCount: PropTypes.number,
		offerRequired: PropTypes.bool,
		skippedMissingVariables: PropTypes.shape({ count: PropTypes.number, byKey: PropTypes.object }),
	}),
	isLoading: PropTypes.bool,
	isError: PropTypes.bool,
	onRetry: PropTypes.func,
	isSending: PropTypes.bool,
};
